"use client";

/**
 * CTA click tracking.
 *
 * Was a no-op stub, which is why the CTA tab, the per-session CTA markers in the
 * Sessions journey, get_content_performance's assisted_conversions and the Leads
 * journey section were all permanently empty -- signup_events had no writer at all.
 *
 * ~17 call sites across the marketing site already pass a destination, a label and
 * a { section, location } context (SiteHeader, LandingHero, FeatureHeroTemplate,
 * ProductSelectionModal, SocialShare, ...), so implementing this here lights all of
 * them up without touching a single component.
 *
 * Posts to /api/track/event so bot classification happens server-side and the
 * funnel does not inherit crawler traffic. Identity comes from
 * src/lib/visitorIdentity.ts, shared with the page-view tracker, so a click and the
 * view it happened on land on the same session_id -- which is the column every
 * funnel RPC joins on.
 */

import { getDeviceType, getSessionId } from "@/lib/visitorIdentity";

export function trackCtaClick(
  destinationLink: string,
  buttonLabel: string,
  sourcePage?: string,
  context?: { section?: string; location?: string }
): void {
  if (typeof window === "undefined") return;
  // Admin screens (the feature-page preview renders the real hero buttons) are not
  // visits; their clicks used to land in the CTA tab as if a visitor made them.
  if (isAdminPath(window.location.pathname)) return;

  noteTracked();

  // Never let analytics break the click it is recording: this runs before the
  // navigation in most handlers, so a throw here would leave the button dead.
  try {
    send(destinationLink, buttonLabel, sourcePage, context);
  } catch {
    /* analytics must never break a navigation */
  }
}

/**
 * One click, one event. The site-wide link recorders (useProductLinkTracking,
 * useSiteLinkTracking) listen in the bubble phase on document, which runs after every
 * React onClick for the same click. If an explicit handler -- or the banner listener --
 * already recorded it, they skip it. The flag is cleared on the next macrotask, which
 * cannot run until the click has finished dispatching.
 */
let trackedThisClick = false;
function noteTracked(): void {
  if (trackedThisClick) return;
  trackedThisClick = true;
  setTimeout(() => {
    trackedThisClick = false;
  }, 0);
}
export function wasClickTracked(): boolean {
  return trackedThisClick;
}

/** /platform is the admin app -- nothing there is a visitor action. */
export function isAdminPath(pathname: string): boolean {
  return pathname === "/platform" || pathname.startsWith("/platform/");
}

function send(
  destinationLink: string,
  buttonLabel: string,
  sourcePage: string | undefined,
  context: { section?: string; location?: string } | undefined,
): void {
  // `action` is what get_cta_attribution groups by, so the human-readable label is
  // the right value -- a URL there would produce one row per destination and read
  // as noise in the dashboard.
  const payload = {
    step: "cta_click",
    action: buttonLabel,
    session_id: getSessionId(),
    device_type: getDeviceType(),
    metadata: {
      // metadata.page is read by get_cta_attribution; label by the Leads journey.
      page: sourcePage || window.location.pathname,
      label: buttonLabel,
      destination: destinationLink,
      section: context?.section,
      location: context?.location,
    },
  };

  // keepalive because most CTAs navigate immediately -- without it the request is
  // cancelled as the page unloads and the click is lost.
  fetch("/api/track/event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    /* analytics must never break a navigation */
  });
}

/**
 * Still a pass-through. Cross-domain decoration (attaching the session id and
 * first-touch UTMs so a registration in app/hrms/assessments can be joined back to
 * the visit) is Q3 of the analytics queue and belongs in src/lib/productDomains.ts,
 * where all five product-link callers already route.
 */
export function getDecoratedProductLink(url: string): string {
  return url;
}
